import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function PrivacyPolicyPage() {
  useMetaTags({
    title: 'Privacy Policy | FindOnlineDegrees',
    description: 'Privacy policy for FindOnlineDegrees.org'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-835">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-836">
        <div className="max-w-4xl mx-auto text-center" data-id="element-837">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-838">
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-839">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-840">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-841">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-842">
            <p data-id="element-843">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-844">
              <li data-id="element-845">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-846">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-847">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          {/* Summary of Key Points */}
          <div data-id="element-848">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-849">
              SUMMARY OF KEY POINTS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-850">
              <ul className="list-disc pl-6 space-y-2" data-id="element-851">
                <li data-id="element-852">
                  <strong data-id="element-853">What personal information do we process?</strong> When
                  you visit, use, or navigate our services, we may process
                  personal information depending on how you interact with us,
                  the choices you make, and the features you use.
                </li>
                <li data-id="element-854">
                  <strong data-id="element-855">Do we process sensitive personal information?</strong>{' '}
                  We do not process sensitive personal information.
                </li>
                <li data-id="element-856">
                  <strong data-id="element-857">Do we receive information from third parties?</strong>{' '}
                  We do not receive information from third parties.
                </li>
                <li data-id="element-858">
                  <strong data-id="element-859">How do we process your information?</strong> To
                  provide, improve, and administer our services, communicate
                  with you, ensure security and fraud prevention, comply with
                  law, and with your consent where required.
                </li>
                <li data-id="element-860">
                  <strong data-id="element-861">
                    When and with whom do we share personal information?
                  </strong>{' '}
                  Only in specific circumstances (e.g., business transfers or
                  service providers).
                </li>
                <li data-id="element-862">
                  <strong data-id="element-863">How do we keep your information safe?</strong> With
                  organizational and technical measures, but no method is 100%
                  secure.
                </li>
                <li data-id="element-864">
                  <strong data-id="element-865">What are your rights?</strong> Depending on your
                  location, you may have rights under applicable privacy laws.
                </li>
                <li data-id="element-866">
                  <strong data-id="element-867">How do you exercise your rights?</strong> You can
                  exercise rights by contacting us (see Section 11).
                </li>
              </ul>
            </div>
          </div>

          {/* Table of Contents */}
          <div data-id="element-868">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-869">
              TABLE OF CONTENTS
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-870">
              <li data-id="element-871">WHAT INFORMATION DO WE COLLECT?</li>
              <li data-id="element-872">HOW DO WE PROCESS YOUR INFORMATION?</li>
              <li data-id="element-873">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
              <li data-id="element-874">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
              <li data-id="element-875">HOW LONG DO WE KEEP YOUR INFORMATION?</li>
              <li data-id="element-876">HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
              <li data-id="element-877">DO WE COLLECT INFORMATION FROM MINORS?</li>
              <li data-id="element-878">WHAT ARE YOUR PRIVACY RIGHTS?</li>
              <li data-id="element-879">CONTROLS FOR DO-NOT-TRACK FEATURES</li>
              <li data-id="element-880">DO WE MAKE UPDATES TO THIS NOTICE?</li>
              <li data-id="element-881">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            </ol>
          </div>

          {/* 1. What Information Do We Collect */}
          <div data-id="element-882">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-883">
              1. WHAT INFORMATION DO WE COLLECT?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-884">
              <h3 className="font-bold text-lg" data-id="element-885">
                Personal information you disclose to us
              </h3>
              <p data-id="element-886">
                We collect personal information that you voluntarily provide to
                us when you express an interest in obtaining information about
                us or our services, when you participate in activities on the
                services, or otherwise when you contact us.
              </p>
              <p data-id="element-887">
                <strong data-id="element-888">Personal Information Provided by You.</strong> The
                personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-889">
                <li data-id="element-890">Names</li>
                <li data-id="element-891">Phone numbers</li>
                <li data-id="element-892">Email addresses</li>
                <li data-id="element-893">Mailing addresses</li>
                <li data-id="element-894">Education level and interests</li>
                <li data-id="element-895">Program preferences</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-896">
                Information automatically collected
              </h3>
              <p data-id="element-897">
                We automatically collect certain information when you visit,
                use, or navigate the services. This information does not reveal
                your specific identity but may include device and usage
                information, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-898">
                <li data-id="element-899">IP address</li>
                <li data-id="element-900">Browser and device characteristics</li>
                <li data-id="element-901">Operating system</li>
                <li data-id="element-902">Language preferences</li>
                <li data-id="element-903">Referring URLs</li>
                <li data-id="element-904">Device name and type</li>
                <li data-id="element-905">Pages viewed and time spent</li>
                <li data-id="element-906">Clickstream data</li>
              </ul>
            </div>
          </div>

          {/* 2. How Do We Process Your Information */}
          <div data-id="element-907">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-908">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-909">
              <p data-id="element-910">
                We process your personal information for a variety of reasons,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-911">
                <li data-id="element-912">To facilitate account creation and authentication</li>
                <li data-id="element-913">To deliver and facilitate delivery of services to you</li>
                <li data-id="element-914">To respond to user inquiries and offer support</li>
                <li data-id="element-915">To send administrative information to you</li>
                <li data-id="element-916">To fulfill and manage your requests</li>
                <li data-id="element-917">To enable user-to-user communications</li>
                <li data-id="element-918">
                  To request feedback and contact you about your use of our
                  services
                </li>
                <li data-id="element-919">To send you marketing and promotional communications</li>
                <li data-id="element-920">To deliver targeted advertising to you</li>
                <li data-id="element-921">
                  To protect our services and investigate potential violations
                </li>
                <li data-id="element-922">
                  To identify usage trends and determine the effectiveness of
                  campaigns
                </li>
                <li data-id="element-923">To save or protect an individual's vital interest</li>
                <li data-id="element-924">To comply with our legal obligations</li>
              </ul>
            </div>
          </div>

          {/* 3. When and With Whom Do We Share */}
          <div data-id="element-925">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-926">
              3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-927">
              <p data-id="element-928">We may share information in the following situations:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-929">
                <li data-id="element-930">
                  <strong data-id="element-931">Business Transfers:</strong> In connection with, or
                  during negotiations of, any merger, sale of company assets,
                  financing, or acquisition.
                </li>
                <li data-id="element-932">
                  <strong data-id="element-933">Educational Institutions:</strong> We share your
                  information with schools and educational partners that match
                  your interests.
                </li>
                <li data-id="element-934">
                  <strong data-id="element-935">Service Providers:</strong> With third-party vendors,
                  service providers, contractors, or agents who perform services
                  for us.
                </li>
                <li data-id="element-936">
                  <strong data-id="element-937">Advertising Partners:</strong> With advertising
                  partners to deliver targeted ads.
                </li>
                <li data-id="element-938">
                  <strong data-id="element-939">Legal Requirements:</strong> When required by law or
                  to protect rights, safety, and property.
                </li>
              </ul>
            </div>
          </div>

          {/* 4. Cookies and Tracking */}
          <div data-id="element-940">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-941">
              4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-942">
              <p data-id="element-943">
                Yes, we use cookies and similar tracking technologies to access
                or store information. See our Cookie Notice for more details
                about how we use cookies.
              </p>
            </div>
          </div>

          {/* 5. How Long Do We Keep */}
          <div data-id="element-944">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-945">
              5. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-946">
              <p data-id="element-947">
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this privacy notice unless otherwise
                required by law. When we have no ongoing legitimate business
                need to process your personal information, we will delete or
                anonymize it.
              </p>
            </div>
          </div>

          {/* 6. How Do We Keep Information Safe */}
          <div data-id="element-948">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-949">
              6. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-950">
              <p data-id="element-951">
                We have implemented appropriate technical and organizational
                security measures designed to protect the security of any
                personal information we process. However, no electronic
                transmission over the internet or information storage technology
                can be guaranteed to be 100% secure.
              </p>
            </div>
          </div>

          {/* 7. Minors */}
          <div data-id="element-952">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-953">
              7. DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-954">
              <p data-id="element-955">
                We do not knowingly solicit data from or market to children
                under 18 years of age. If you become aware that a child has
                provided us with personal information, please contact us. If we
                learn we have collected personal information from a child under
                18, we will delete that information.
              </p>
            </div>
          </div>

          {/* 8. Privacy Rights */}
          <div data-id="element-956">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-957">
              8. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-958">
              <p data-id="element-959">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-960">
                <li data-id="element-961">The right to access your personal information</li>
                <li data-id="element-962">
                  The right to request correction of your personal information
                </li>
                <li data-id="element-963">
                  The right to request deletion of your personal information
                </li>
                <li data-id="element-964">The right to opt out of marketing communications</li>
                <li data-id="element-965">
                  The right to opt out of the sale or sharing of personal
                  information (where applicable)
                </li>
              </ul>
              <p className="mt-4" data-id="element-966">
                To exercise these rights, please contact us using the
                information in Section 11.
              </p>
            </div>
          </div>

          {/* 9. Do Not Track */}
          <div data-id="element-967">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-968">
              9. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-969">
              <p data-id="element-970">
                Most web browsers and some mobile operating systems include a
                Do-Not-Track ("DNT") feature or setting you can activate to
                signal your privacy preference. At this stage, no uniform
                technology standard for recognizing and implementing DNT signals
                has been finalized.
              </p>
            </div>
          </div>

          {/* 10. Updates */}
          <div data-id="element-971">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-972">
              10. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-973">
              <p data-id="element-974">
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws. The updated version will be indicated by an
                updated "Last updated" date at the top of this privacy notice.
              </p>
            </div>
          </div>

          {/* 11. Contact Us */}
          <div data-id="element-975">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-976">
              11. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-977">
              <p data-id="element-978">
                If you have questions or comments about this notice, please
                contact us through the information provided on our website.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}